<script lang="ts">
	import { onMount } from 'svelte';
	import { get } from 'svelte/store';
	import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { devices } from 'src/stores/lists/devices';
	import { _deepCopyArr, conditionsFunc } from '../../../../../../../../helpers/utilities';
	import Timer from './Components/TimerV2.svelte';
	import { DO_WIZARD_STEPS_INDEXES } from '../../helpers/do-wizard-helpers';
	import { roller_is_calibrated } from 'src/helpers/requests/calibrate_cover';
	import { isVCType } from 'src/components/Scenes/helpers/virtual_components/virtual_component_utils';
	import { validateSelectedVCAction } from 'src/components/Scenes/helpers/virtual_components/virtual_device_actions';
	import { bootstrapEditedDeviceAction } from '../../../Device/helpers';

	export let sceneData: any;
	let showToggleWarning: boolean = false;

	bootstrapEditedDeviceAction(sceneData, get(devices));

	const TILT_ACTION_NAMES = ['blinds_tilt_position', 'multi_cover_blinds_tilt_position'];

	$: statusStore = getDeviceStatus(sceneData.selectedDevice.id);
	$: settingsStore = getDeviceSettings(sceneData.selectedDevice.id);

	$: isRollerCalibrated = roller_is_calibrated(
		$devices[sceneData.selectedDevice.id],
		$statusStore,
		$settingsStore,
	);

	$: transitionDurationValue = sceneData?.deviceAction?.actions?.find(
		(action) => action.name === 'transition_duration',
	)?.value;

	/**
	 * Detects the case where an existing action references a feature that is no
	 * longer exposed by the device — currently the blinds tilt option, which
	 * disappears when the user disables the slat feature on the cover device.
	 *
	 * In that case the UI shows no selected card, so we force the user to pick
	 * another option before they can leave the step.
	 */
	const isSelectedActionUnavailable = () => {
		const activeName = sceneData?.deviceAction?.actions?.[0]?.name;
		if (!TILT_ACTION_NAMES.includes(activeName)) return false;

		const availableActionIds: string[] = (sceneData?.deviceAction?.components ?? [])
			.flatMap((c: any) => c?.actions ?? [])
			.map((a: any) => a?.id);

		return !availableActionIds.includes(activeName);
	};

	const computeChangeStateValid = (): boolean => {
		if (isVCType(sceneData?.deviceAction?.actions?.[0]?.name)) {
			if (import.meta.env.DEV) {
				console.info('[Wizard step] Validate VC Action', sceneData.deviceAction.actions[0]);
			}
			return validateSelectedVCAction(sceneData.deviceAction.actions[0]);
		}

		if (
			sceneData.deviceAction.actions.length === 0 ||
			(!sceneData.deviceAction?.actions?.[0]?.value &&
				typeof sceneData.deviceAction?.actions?.[0]?.value === 'string') ||
			conditionsFunc(sceneData.deviceAction.timer.value, false, true) ||
			(sceneData.deviceAction.actions.find((action) => action.name === 'transition_duration') &&
				(sceneData.deviceAction.actions.length <= 1 ||
					+transitionDurationValue < 0.5 ||
					+transitionDurationValue > 10800)) ||
			isSelectedActionUnavailable()
		) {
			return false;
		}

		return true;
	};

	const validateWizardStepChangeState = () => {
		const nextValid = computeChangeStateValid();
		if (sceneData.isValid !== nextValid) {
			sceneData.isValid = nextValid;
		}
		if (sceneData.validStepIndex !== DO_WIZARD_STEPS_INDEXES.CHANGE_STATE) {
			sceneData.validStepIndex = DO_WIZARD_STEPS_INDEXES.CHANGE_STATE;
		}
	};

	if (sceneData.deviceAction.components.length > 0 && sceneData.do_info.mode === 'edit') {
		sceneData.deviceAction.components.forEach((obj) => {
			if (
				obj?.actions[0]?.hasOwnProperty('addOns') &&
				obj?.actions[0]?.hasOwnProperty('alert') &&
				obj?.actions[0]?.addOns?.length <= 0 &&
				obj?.actions[0]?.alert !== ''
			) {
				if (sceneData.deviceAction.actions.length > 0) {
					sceneData.deviceAction.actions = [];
				}
			}
		});
	}

	onMount(validateWizardStepChangeState);
</script>

<!--every child component has to has  sceneIsValid because if there is'n sceneIsValid returns "undefined" and edit wizard showing disabled tabs and next TempButtonsTypes, even if the scene is valid -->
<!-- binding active actions because on previous next and then back sometimes they are not loaded. ToDo need to use something else maybe. -->
{#if sceneData.deviceAction.components.length > 0}
	{#each sceneData.deviceAction.components as _el, _ind}
		<svelte:component
			this={_el.component}
			actions={_el.actions}
			bind:sceneIsValid={sceneData.isValid}
			bind:active_actions={sceneData.deviceAction.actions}
			mode={sceneData.do_info.mode}
			deviceId={sceneData.selectedDevice.id}
			{sceneData}
			{isRollerCalibrated}
			on:change_active={(e) => {
				sceneData.deviceAction.actions = _deepCopyArr(e.detail.new_active);
				validateWizardStepChangeState();
			}}
		/>
	{/each}
{/if}

{#if ['relay', 'emeter', 'module'].includes($devices[sceneData.selectedDevice.value]?.category)}
	<Timer bind:sceneData {showToggleWarning} active_actions={sceneData.deviceAction.actions} />
{/if}
